'use client'

import React from 'react';
import { builder, BuilderContent } from "@builder.io/sdk";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState } from "react";
import { Skeleton } from '@/components/ui/skeleton';
import { SpacingType, sectionVariants } from '@/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';
import { PageContext } from '../builder';
import { useDebounce } from '@/hooks/useDebounce';
import { SectionWrapper } from '../components/SectionWraper';
import { EmptyResult } from '../components/EmptyResult';
import { cn, variantBackgroundColors } from '@/lib/utils';

interface CaseStudiesListProps {
  spacing?: SpacingType;
  darkMode?: boolean; 
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

interface ProductData {
  product?: string;
  link?: string;
}

interface IndustryData {
  industry?: string;
  link?: string;
}

interface BuilderQuery {
  'data.products'?: {
    $elemMatch: {
      product: string;
    };
  };
  $or?: Array<{
    name?: { $regex: string; $options: string };
    'data.location'?: { $regex: string; $options: string };
    'data.industry.industry'?: { $regex: string; $options: string };
    'data.products.product'?: { $regex: string; $options: string };
  }>;
}

export function CaseStudiesList({ spacing = 'xlarge', darkMode = false, variant = 'A', usedInPageNav = false, pageNavTitle = 'Case Studies', themeChangeOnScroll = false, attributes }: CaseStudiesListProps) {
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fetchedItems, setFetchedItems] = useState<BuilderContent[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [query, setQuery] = useState<BuilderQuery>({});
  const debouncedQuery = useDebounce(query, 500);

  const pageFields = React.useContext(PageContext);
  const { productsData, industriesData, articlesData, itemsPerPage } = (pageFields.data as unknown as { productsData?: ProductData[]; industriesData?: IndustryData[]; articlesData?: BuilderContent[]; itemsPerPage?: number }) ?? {};

  // Update query when filters or search changes
  useEffect(() => {
    const newQuery: BuilderQuery = {};
    
    if (selectedProduct && selectedProduct !== 'all') {
      newQuery['data.products'] = {
        $elemMatch: {
          product: selectedProduct
        }
      };
    }

    if (debouncedSearchTerm) {
      newQuery.$or = [
        { name: { $regex: debouncedSearchTerm, $options: 'i' } },
        { 'data.location': { $regex: debouncedSearchTerm, $options: 'i' } },
        { 'data.industry.industry': { $regex: debouncedSearchTerm, $options: 'i' } },
        { 'data.products.product': { $regex: debouncedSearchTerm, $options: 'i' } }
      ];
    }

    setQuery(newQuery);
  }, [selectedProduct, debouncedSearchTerm]);

  // Use debouncedQuery for data fetching
  const fetchFilteredData = React.useCallback(async () => {
    setLoading(true);
    try {
      const articles = await builder.getAll('case-study', {
        limit: (itemsPerPage ?? 0) + 1,
        query: debouncedQuery,
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });

      setFetchedItems(articles.slice(0, itemsPerPage ?? 0));
      setHasMore(articles.length > (itemsPerPage ?? 0));
      setLoading(false);
    } catch (err) {
      console.error("Filter fetch failed", err);
      setLoading(false);
    }
  }, [debouncedQuery, itemsPerPage]);
  
  const loadMore = React.useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const newOffset = offset + (itemsPerPage ?? 0);
      
      const newArticles = await builder.getAll('case-study', {
        limit: (itemsPerPage ?? 0) + 1,
        offset: newOffset,
        query: debouncedQuery,
        options: {
          noTargeting: true,
        },
        preview: true,
        noTraverse: true,
        noEditorUpdates: true,
        prerender: false,
      });

      if (newArticles.length > 0) {
        setFetchedItems(prev => [...prev, ...newArticles.slice(0, itemsPerPage ?? 0)]);
        setOffset(newOffset);
        setHasMore(newArticles.length > (itemsPerPage ?? 0));
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Load more failed", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [offset, itemsPerPage, debouncedQuery]);

  // Initial data load and filter updates
  useEffect(() => {
    // If we have articlesData from context, use it for initial load
    if (articlesData && !debouncedQuery.$or && !debouncedQuery['data.products']) {
      setFetchedItems(articlesData.slice(0, itemsPerPage ?? 0) || []);
      setHasMore(articlesData.length > (itemsPerPage ?? 0));
      setLoading(false);
    } else {
      // Otherwise fetch with current filters
      fetchFilteredData();
    }
  }, [articlesData, itemsPerPage, debouncedQuery, fetchFilteredData]);

  useEffect(() => {
    if (fetchedItems?.length > 0) {
      setLoading(false);
    }
  }, [fetchedItems]);

  const inputBackgroundColor = variantBackgroundColors(darkMode, variant);

  if (loading) {
    return (
      <SectionWrapper
        usedInPageNav={usedInPageNav}
        pageNavTitle={pageNavTitle}
        themeChangeOnScroll={themeChangeOnScroll}
        spacing={spacing}
        variant={variant}
        darkMode={darkMode}
        heroSection={false}
        componentName="case-studies-list"
        attributes={attributes}
      >
        <div className="container" data-aos="fade-up">
          <h2>
            Customer stories.
          </h2>
          <div className="flex flex-col md:flex-row gap-4 md:gap-10 justify-between py-8 md:py-16">
            <Skeleton className={cn(inputBackgroundColor, "h-14 w-full md:w-70")} />
            <Skeleton className={cn(inputBackgroundColor, "h-14 w-full md:w-80")} />   
          </div>
          <div className="">
            <div className="flex flex-col gap-8 pt-0 md:pt-8">
              {[...Array(6)].map((_, index) => (
                <React.Fragment key={index}>
                  <div className="grid grid-cols-1 gap-12 lg:grid-cols-4">
                    <div className="items-center self-start lg:flex">
                      <div className="flex flex-col gap-1 w-full">
                        <Skeleton className={cn(inputBackgroundColor, "h-7 w-[80%]")} />
                        <Skeleton className={cn(inputBackgroundColor, "h-6 w-[50%]")} />
                      </div>
                    </div>
                    <div className="col-span-2 max-w-2xl">
                      <Skeleton className={cn(inputBackgroundColor, "h-8 w-[100%] mb-2")} />   
                      <Skeleton className={cn(inputBackgroundColor, "h-8 w-[80%]")} />   
                      <div className="mt-8 flex flex-wrap gap-2">
                        {[...Array(2)].map((_, catIndex) => (
                          <Skeleton key={catIndex} className={cn(inputBackgroundColor, "h-8 w-24 rounded-full")} />
                        ))}
                      </div>
                    </div>
                    <div className="items-center justify-end hidden lg:flex">
                      <Skeleton className={cn(inputBackgroundColor, "h-14 w-14 rounded-full hidden lg:block ml-auto")} />   
                    </div>
                  </div>
                  <Separator orientation="horizontal" />
                </React.Fragment>
              ))}
            </div>
            <div className="mt-16">
              <Skeleton className={cn(inputBackgroundColor, "h-14 w-50 rounded-full hidden lg:block m-auto")} />   
            </div>
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="case-studies-list"
      attributes={attributes}
    >
      <div className="container" data-aos="fade-up">
        <h2>
          Customer stories.
        </h2>
        <div className="flex flex-col md:flex-row gap-4 md:gap-10 justify-between py-8 md:py-16">
          <Select value={selectedProduct} onValueChange={(value) => {
            setSelectedProduct(value);
          }}>
            <SelectTrigger className={cn(inputBackgroundColor, "w-full md:w-70")}>
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {productsData?.map((product: ProductData) => (
                <SelectItem key={product.product} value={product.product ?? ""}>
                  {product.product}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Input 
              type="text" 
              placeholder="Search"  
              className={cn(inputBackgroundColor, "w-full md:w-80 pl-9")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="material-symbols-outlined w-4.5 h-4.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ fontSize: '18px' }}>search</span>
          </div>
        </div>
        <div className="">
          <div className="flex flex-col gap-8 pt-0 md:pt-8">
            {fetchedItems && fetchedItems.length > 0 ? (
              fetchedItems.map((item: BuilderContent) => (
                <React.Fragment key={item.id}>
                  <div className="grid grid-cols-1 gap-12 lg:grid-cols-4">
                    <div className="items-center self-start flex">
                      <div className="flex flex-col gap-1 w-full content-accordion">
                        {item.data?.industry && item.data?.industry.length > 0 && (
                          (() => {
                            const foundIndustry = industriesData?.find(
                              (ind: IndustryData) => ind.industry === item.data?.industry[0]?.industry
                            );
                            return foundIndustry ? (
                              <Link href={foundIndustry.link ?? ""} tabIndex={-1} className="paragraph-lg font-bold">
                                {foundIndustry.industry}
                              </Link>
                            ) : (
                              <span className="paragraph-lg font-bold">
                                {item.data?.industry[0]?.industry}
                              </span>
                            );
                          })()
                        )}
                        {item.data?.location && (
                          <span className="paragraph-base text-muted-foreground">
                            {item.data?.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 max-w-2xl">
                      <Link href={item.data?.url ?? ""} tabIndex={-1} className="paragraph-lg font-bold">
                        <h4 className="">{item?.data?.title}</h4>
                      </Link>
                      {item.data?.products && item.data?.products.length > 0 && (
                        <div className="mt-8 flex flex-wrap gap-2">
                          {item?.data?.products?.map((product: ProductData) => (
                            <Badge variant="secondary" key={product.product} bold>{product.product}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="items-center justify-end hidden lg:flex">
                      <Button 
                        variant="outline" 
                        asChild
                        size="iconLg"
                        lifted
                        grayBorder
                      >
                        <Link href={item.data?.url ?? ""}>
                          <span className="material-symbols-outlined w-6 h-6" style={{ fontSize: '24px' }}>arrow_forward</span>
                        </Link>
                      </Button>
                    </div>
                  </div>
                  <Separator orientation="horizontal" />
                </React.Fragment>
              ))
            ) : (
              <EmptyResult
                className="mt-16"
                buttonOnClick={() => {
                  setLoading(true);
                  setSearchTerm("");
                  setSelectedProduct("all");
                  // Force query update with empty search
                  setQuery({});
                }}
              />
            )}
          </div>
          {hasMore && (
          <div className="mt-16 text-center">
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full md:w-auto cursor-pointer"
              onClick={loadMore}
              disabled={!hasMore || isLoadingMore}
            > 
              {isLoadingMore ? 'Loading...' : hasMore ? 'Load more articles' : 'No more articles'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
} 